/**
 * Browser copy of api/src/medbin/xmlObjectCodes.ts.
 * Coolify builds only the web directory, so this screen cannot import ../api.
 *
 * XML ObjectCode / ObjectExtensionCode for XBIN and the parent that cites it.
 *
 * The extension → OBJEKTTYPE:OBJEKTEXTENSION map in edi.ts is the only
 * table. This file translates that pair into the vocabulary of
 * ObjectCodeType / ObjectExtensionCodeType in MedCom's GeneralTypes.xsd for
 * the XREF01 namespace http://rep.oio.dk/medcom.dk/xml/schemas/2017/05/01/
 * (Schemas/Konverter/Konverter/xml/Schemas/2017/05/01/GeneralTypes.xsd).
 * It does not invent a code the schema does not enumerate.
 *
 * ObjectCode is the Danish long form of the EDI OBJEKTTYPE. The schema
 * enumerates seven values; the EDI short forms align as:
 *   TXT → tekstfil, IMG → billede, PRG → program, VGR → vektor_grafik,
 *   BSG → biosignaler, MUL → multimedie, PRP → proprietaert_indhold.
 * This map deliberately omits program: executables and other PRG pairings
 * stay out of scope even though the schema permits them.
 *
 * The Spor A example corpus (counted 2026-09-28) uses only four ObjectCode
 * values in practice:
 *   billede        45  — jpeg 22, png 11, tiff 8, bmp 3, and one pdf
 *   vektor_grafik  16  — pdf
 *   multimedie      3  — wav
 *   biosignaler     3  — scp
 * That corpus is still useful for naming conventions (JPG → jpeg, TIF →
 * tiff) and for the SND → multimedie discrepancy below, but it is not the
 * authority for which ObjectCode values exist. The schema is.
 *
 * The single billede + pdf is XBIN_pdf_example.xml in the XDIS91
 * korrespondance examples. Sixteen other pdfs are vektor_grafik. That one
 * file is treated as an anomaly and is not followed: pdf stays VGR.
 *
 * ObjectExtensionCode is the long lowercase form, not the EDI short form:
 *   JPG → jpeg (not jpg), TIF → tiff (not tif).
 * png, bmp, pdf, wav, scp, gif, dcm, txt, rtf, doc, xls and zip are already
 * the form the schema enumerates.
 *
 * MedCom (Rasmus Skov Møller Hansen, Standardteam, 2026-09-29) confirmed in
 * writing the EDI pairings IMG:GIF, IMG:PNG, IMG:DCM, TXT:TXT, TXT:RTF,
 * TXT:DOC and TXT:XLS. Those resolve here to billede/gif, billede/png,
 * billede/dcm, and tekstfil/{txt,rtf,doc,xls}.
 *
 * Known discrepancy, same kind edi.ts already records for SND:
 *   BIN_HJERTELYD.edi sends OBJ:SND:WAV, and SND is absent from the
 *   Kvalifikatorliste enumeration. The XML examples qualify wav as
 *   multimedie. XML therefore renders SND as multimedie.
 *
 * Zip stays typeless on the EDI side ("kan kvalificeres med alle
 * OBJEKTTYPER"), but XML ObjectCode is mandatory. A caller that knows the
 * content passes objectType into xmlObjectCodes the same way edi.ts accepts
 * it; without that override zip is refused. PRP → proprietaert_indhold is
 * mapped so a typed zip can render. Schema-permitted extensions that this
 * project has not enabled (exe, com, bin, plo, inh, wpd, pcx, mpg, avi,
 * mid, rmi) remain refused.
 */

import { ediObjectPair, type EdiObjectPair } from "./edi";

const XML_OBJECT_TYPE: Record<string, string> = {
  IMG: "billede",
  VGR: "vektor_grafik",
  MUL: "multimedie",
  BSG: "biosignaler",
  SND: "multimedie",
  TXT: "tekstfil",
  PRP: "proprietaert_indhold",
};

/** OBJEKTEXTENSION → ObjectExtensionCode. Absent means deliberately unmapped. */
const XML_EXTENSION: Record<string, string> = {
  JPG: "jpeg",
  PNG: "png",
  TIF: "tiff",
  BMP: "bmp",
  PDF: "pdf",
  WAV: "wav",
  SCP: "scp",
  GIF: "gif",
  DCM: "dcm",
  TXT: "txt",
  RTF: "rtf",
  DOC: "doc",
  XLS: "xls",
  ZIP: "zip",
};

export type XmlObjectCodes = {
  objectCode: string;
  objectExtensionCode: string;
};

export class XmlObjectCodeError extends Error {
  readonly code = "xml-object-code-unmapped" as const;
  readonly extension: string;

  constructor(extension: string, message: string) {
    super(message);
    this.name = "XmlObjectCodeError";
    this.extension = extension;
  }
}

function refuse(extension: string, message: string): never {
  throw new XmlObjectCodeError(extension, message);
}

function unmappedReason(extension: string, pair: EdiObjectPair): string {
  const renderedType = XML_OBJECT_TYPE[pair.objectType];
  const renderedExtension = XML_EXTENSION[pair.objectExtension];
  if (renderedType === undefined) {
    return `.${extension} is ${pair.objectType}:${pair.objectExtension} in the EDI map, but OBJEKTTYPE ${pair.objectType} is not mapped to an XML ObjectCode in this project, so it cannot be sent as XML.`;
  }
  if (renderedExtension === undefined) {
    return `.${extension} is ${pair.objectType}:${pair.objectExtension} in the EDI map, but OBJEKTEXTENSION ${pair.objectExtension} is not enabled as an XML ObjectExtensionCode in this project, so it cannot be sent as XML.`;
  }
  return `.${extension} is ${pair.objectType}:${pair.objectExtension} in the EDI map, which has no XML form in this project, so it cannot be sent as XML.`;
}

/**
 * Resolve ObjectCode / ObjectExtensionCode for a file extension.
 * `objectType` is the same override edi.ts accepts: required for zip, optional
 * elsewhere for a caller who knows better than the default pairing.
 */
export function xmlObjectCodes(fileExtension: string, objectType?: string): XmlObjectCodes {
  const extension = fileExtension.trim().toLowerCase().replace(/^\./, "");
  if (extension.length === 0) {
    refuse(extension, "A file extension is required to choose ObjectCode and ObjectExtensionCode.");
  }
  let pair: EdiObjectPair;
  try {
    pair = ediObjectPair(extension, objectType);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    const sentence = /[.!?]$/.test(detail) ? detail : `${detail}.`;
    const xmlNote = extension === "zip"
      ? " XML ObjectCode is mandatory, so zip cannot be sent as XML until the caller names an OBJEKTTYPE."
      : " This file cannot be sent as XML.";
    refuse(extension, `${sentence}${xmlNote}`);
  }
  const objectCode = XML_OBJECT_TYPE[pair.objectType];
  const objectExtensionCode = XML_EXTENSION[pair.objectExtension];
  if (objectCode === undefined || objectExtensionCode === undefined) {
    refuse(extension, unmappedReason(extension, pair));
  }
  return { objectCode, objectExtensionCode };
}
